/* YŪ SEI library: stable entry IDs, legacy-link aliases and the existing drawer.
   Titles, photographs and related references are rendered from live metaobjects.
   No static article-name list and no content copied into JavaScript. */
(() => {
  'use strict';
  const key = '__yuMetaobjectArticleLibrary';
  if (window[key]) { window[key].init(document); return; }
  const records = new Map();
  function mount(section) {
    if (records.has(section)) return;
    const drawer = section.querySelector('[data-yu-ritual-drawer]');
    const panel = section.querySelector('[data-yu-ritual-panel]');
    const content = section.querySelector('[data-yu-ritual-content]');
    const grid = section.querySelector('[data-yu-library-cards]');
    const status = section.querySelector('[data-yu-library-status]');
    if (!drawer || !panel || !content || !grid) return;
    const controller = new AbortController();
    const listen = (target, type, fn) => target.addEventListener(type, fn, {signal:controller.signal});
    let closeTimer = null, lastTrigger = null, loading = null, requestVersion = 0;
    const visited = new Set();
    const nextLink = () => section.querySelector('[data-yu-library-next] a');
    function orderCards() {
      Array.from(grid.querySelectorAll(':scope > [data-yu-library-card]'))
        .sort((a,b) => Number(a.dataset.yuOrder || 999999) - Number(b.dataset.yuOrder || 999999))
        .forEach(card => { grid.append(card); const template = section.querySelector('#'+CSS.escape(card.dataset.templateId)); if(template) grid.append(template); });
    }
    orderCards();
    function showStatus(message) { if(status) { status.textContent = message; status.hidden = !message; } }
    function findButton(value) {
      return Array.from(grid.querySelectorAll('[data-yu-ritual-open]')).find(button => {
        if (button.dataset.articleKey === value || button.dataset.articleKey?.split('/').pop() === String(value).split('/').pop()) return true;
        try { return JSON.parse(button.dataset.articleAliases || '[]').includes(value); } catch (_) { return false; }
      });
    }
    async function loadNext() {
      if(loading) return loading;
      const link = nextLink(); if(!link) return false;
      const url = new URL(link.href, location.href);
      if(url.origin !== location.origin || visited.has(url.href)) return false;
      visited.add(url.href);
      loading = (async () => {
        const response = await fetch(url, {signal:controller.signal, credentials:'same-origin'});
        if(!response.ok) throw new Error('Article library could not be loaded.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const other = doc.getElementById(section.id); const otherGrid = other?.querySelector('[data-yu-library-cards]');
        if(!otherGrid) throw new Error('Article library could not be loaded.');
        for(const child of Array.from(otherGrid.children)) {
          if(child.tagName === 'TEMPLATE' && section.querySelector('#'+CSS.escape(child.id))) continue;
          if(child.matches('[data-yu-library-card]') && findButton(child.dataset.articleKey)) continue;
          grid.append(document.importNode(child,true));
        }
        const pager = section.querySelector('[data-yu-library-next]');
        const next = other.querySelector('[data-yu-library-next]');
        if(next) pager?.replaceWith(document.importNode(next,true)); else pager?.remove();
        orderCards(); return true;
      })();
      try{return await loading;} catch(error){visited.delete(url.href);throw error;} finally{loading = null;}
    }
    async function resolve(value) {
      let button = findButton(value);
      while(!button && nextLink()) { if(!(await loadNext())) break; button = findButton(value); }
      return button;
    }
    async function openArticle(value, trigger) {
      const version = ++requestVersion;
      try {
        const button = await resolve(value);
        if(version !== requestVersion || controller.signal.aborted) return;
        if(!button) { showStatus('This article is no longer available. Please choose another read.'); return; }
        const template = section.querySelector('#'+CSS.escape(button.dataset.templateId));
        if(!template) return;
        clearTimeout(closeTimer); closeTimer = null;
        lastTrigger = trigger && !content.contains(trigger) ? trigger : button;
        content.replaceChildren(template.content.cloneNode(true)); panel.scrollTop=0;
        drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden','false');
        document.documentElement.classList.add('yu-ritual-drawer-open'); document.body.classList.add('yu-ritual-drawer-open');
        showStatus('');
        const url = new URL(location.href); url.searchParams.set('article',button.dataset.articleKey);
        history.replaceState(history.state,'',url);
        requestAnimationFrame(() => { if(version===requestVersion) panel.focus({preventScroll:true}); });
      } catch(error) { if(error.name!=='AbortError') showStatus('The article could not be loaded. Please try again.'); }
    }
    function closeArticle() {
      requestVersion++; drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden','true');
      document.documentElement.classList.remove('yu-ritual-drawer-open'); document.body.classList.remove('yu-ritual-drawer-open');
      clearTimeout(closeTimer); closeTimer=setTimeout(()=>{content.replaceChildren();closeTimer=null;},900);
      const url=new URL(location.href);url.searchParams.delete('article');history.replaceState(history.state,'',url);
      if(lastTrigger?.isConnected) lastTrigger.focus({preventScroll:true});
    }
    /* Touchscreen article swipe: reuse closeArticle; never capture native scrolling. */
    const swipeLayout = matchMedia('(any-pointer: coarse)');
    let articleSwipe = null;
    let swipeFocusTarget = null;
    function clearSwipeReturnFocus() {
      if (!swipeFocusTarget) return;
      swipeFocusTarget.removeAttribute('data-yu-swipe-return-focus');
      swipeFocusTarget.removeEventListener('blur', clearSwipeReturnFocus);
      swipeFocusTarget = null;
    }
    function closeArticleFromSwipe() {
      clearSwipeReturnFocus();
      if (lastTrigger?.isConnected && lastTrigger.matches('.yu-ritual-card')) {
        swipeFocusTarget = lastTrigger;
        swipeFocusTarget.setAttribute('data-yu-swipe-return-focus', '');
        swipeFocusTarget.addEventListener('blur', clearSwipeReturnFocus, {once:true, signal:controller.signal});
      }
      closeArticle();
    }
    /* Retain focus after touch dismissal; restore its ring before any keyboard input. */
    document.addEventListener('keydown', clearSwipeReturnFocus, {capture:true, signal:controller.signal});
    controller.signal.addEventListener('abort', clearSwipeReturnFocus, {once:true});
    const swipeIgnored = 'a, button, input, textarea, select, summary, video, audio, iframe, [role="button"], [contenteditable]:not([contenteditable="false"]), [data-yu-related-open], .yu-ritual-related__grid';
    function swipeIsAvailable() {
      return swipeLayout.matches && drawer.classList.contains('is-open') &&
        !(window.visualViewport && window.visualViewport.scale > 1.05) &&
        !(window.getSelection && window.getSelection()?.isCollapsed === false);
    }
    function swipeTargetIsSafe(target) {
      if (!(target instanceof Element) || target.closest(swipeIgnored)) return false;
      for (let node = target; node && node !== panel; node = node.parentElement) {
        if (node.scrollWidth > node.clientWidth + 1 && /^(auto|scroll)$/.test(getComputedStyle(node).overflowX)) return false;
      }
      return true;
    }
    function resetArticleSwipe() { articleSwipe = null; }
    function startArticleSwipe(event) {
      resetArticleSwipe();
      if (!swipeIsAvailable() || event.touches.length !== 1 || !swipeTargetIsSafe(event.target)) return;
      const touch = event.touches[0];
      /* Leave Safari's viewport-edge navigation gesture to the browser. */
      if (touch.clientX <= 24) return;
      articleSwipe = {id:touch.identifier, x:touch.clientX, y:touch.clientY, time:event.timeStamp, scrollTop:panel.scrollTop, version:requestVersion};
    }
    function moveArticleSwipe(event) {
      if (!articleSwipe) return;
      if (!swipeIsAvailable() || event.touches.length !== 1 || panel.scrollTop !== articleSwipe.scrollTop) { resetArticleSwipe(); return; }
      const touch = event.touches[0];
      const dx = touch.clientX - articleSwipe.x, dy = touch.clientY - articleSwipe.y;
      if (touch.identifier !== articleSwipe.id || dx < -12 || (Math.abs(dy) > 12 && Math.abs(dy) >= Math.abs(dx))) resetArticleSwipe();
    }
    function endArticleSwipe(event) {
      const swipe = articleSwipe;
      resetArticleSwipe();
      if (!swipe || !swipeIsAvailable() || event.touches.length || swipe.version !== requestVersion || panel.scrollTop !== swipe.scrollTop) return;
      const touch = Array.from(event.changedTouches).find(item => item.identifier === swipe.id);
      if (!touch) return;
      const dx = touch.clientX - swipe.x, dy = Math.abs(touch.clientY - swipe.y), elapsed = event.timeStamp - swipe.time;
      const distance = Math.max(72, Math.min(120, panel.clientWidth * .22));
      if (elapsed >= 0 && elapsed <= 900 && dx >= distance && dy <= 36 && dx > dy * 2.2) closeArticleFromSwipe();
    }
    panel.addEventListener('touchstart', startArticleSwipe, {passive:true, signal:controller.signal});
    panel.addEventListener('touchmove', moveArticleSwipe, {passive:true, signal:controller.signal});
    panel.addEventListener('touchend', endArticleSwipe, {passive:true, signal:controller.signal});
    panel.addEventListener('touchcancel', resetArticleSwipe, {passive:true, signal:controller.signal});
    /* End touchscreen article swipe. */
    listen(section,'click',event=>{
      const open=event.target.closest('[data-yu-ritual-open], [data-yu-related-open]');
      if(open && section.contains(open)) {event.preventDefault();openArticle(open.dataset.articleKey,open);return;}
      if(event.target.closest('[data-yu-ritual-close]')) {event.preventDefault();closeArticle();return;}
      if(event.target.closest('[data-yu-library-next] a')) {event.preventDefault();loadNext().catch(()=>showStatus('Please try loading the next articles again.'));}
    });
    listen(document,'keydown',event=>{
      if(!drawer.classList.contains('is-open'))return;
      if(event.key==='Escape'){event.preventDefault();closeArticle();}
      if(event.key==='Tab'){
        const items=Array.from(panel.querySelectorAll('button, a[href], input, select, textarea, [tabindex="0"]')).filter(x=>!x.disabled && x.getClientRects().length);
        const first=items[0],last=items[items.length-1];
        if(event.shiftKey&&(document.activeElement===first||document.activeElement===panel)){event.preventDefault();(last||panel).focus();}
        else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();(first||panel).focus();}
      }
    });
    const requested=new URLSearchParams(location.search).get('article');
    if(requested){section.querySelector('#articles')?.scrollIntoView({behavior:'instant',block:'start'});requestAnimationFrame(()=>requestAnimationFrame(()=>openArticle(requested,null)));}
    records.set(section,()=>{controller.abort();clearTimeout(closeTimer);requestVersion++;if(drawer.classList.contains('is-open')){document.documentElement.classList.remove('yu-ritual-drawer-open');document.body.classList.remove('yu-ritual-drawer-open');}records.delete(section);});
  }
  function init(root){if(root.matches?.('[data-yu-ritual-grid]'))mount(root);root.querySelectorAll?.('[data-yu-ritual-grid]').forEach(mount);}
  window[key]={init};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(document),{once:true});else init(document);
  document.addEventListener('shopify:section:load',e=>init(e.target));
  document.addEventListener('shopify:section:unload',e=>{for(const [section,destroy]of records)if(e.target===section||e.target.contains(section))destroy();});
})();
